import { RefObject, useLayoutEffect, useState } from 'react';

export const TIMELINE_CARD_ATTRIBUTE = 'data-timeline-card';

const MIN_PAGE_SIZE = 1;

interface Options {
  /** A single column's card area; its height is the vertical budget for one page of cards. */
  heightRef: RefObject<HTMLElement>;
  /** Root of the board; cards anywhere underneath are measured so the tallest wins. */
  cardsRootRef: RefObject<HTMLElement>;
  /** Vertical gap between cards in px (must match the CSS used to lay them out). */
  gapPx: number;
  /** Any value whose change should trigger a re-measure (e.g. patient count, font tier). */
  deps?: unknown[];
}

/**
 * Computes how many patient cards fit vertically inside a phase column.
 *
 * Mirrors the approach in `usePanelDistribution`: observe the real container height, measure a
 * card that is actually in the DOM, and derive the page size from that rather than guessing from
 * the viewport. One value is shared across all columns so their pages stay visually aligned.
 */
export const useTimelinePageSize = ({ heightRef, cardsRootRef, gapPx, deps = [] }: Options): number => {
  const [pageSize, setPageSize] = useState(MIN_PAGE_SIZE);

  useLayoutEffect(() => {
    const heightEl = heightRef.current;
    const cardsRoot = cardsRootRef.current;
    if (!heightEl || !cardsRoot) return undefined;

    const measure = () => {
      const containerHeight = heightEl.clientHeight;
      // Layout heights exclude the temporary scale applied by PoseGroup's FLIP animation.
      // Measuring that scale can leave the carousel using an oversized, stale card height.
      const cards = cardsRoot.querySelectorAll<HTMLElement>(`[${TIMELINE_CARD_ATTRIBUTE}="true"]`);
      let tallest = 0;
      cards.forEach(card => {
        const h = card.offsetHeight;
        if (Number.isFinite(h) && h > tallest) tallest = h;
      });
      if (containerHeight > 0 && tallest > 0) {
        const fit = Math.floor((containerHeight + gapPx) / (tallest + gapPx));
        setPageSize(Math.max(MIN_PAGE_SIZE, Number.isFinite(fit) ? fit : MIN_PAGE_SIZE));
      }
    };

    const observer = new ResizeObserver(() => measure());
    observer.observe(heightEl);
    observer.observe(cardsRoot);

    // The board's dimensions stay fixed when fonts, icons or patient cards change size.
    // Observe the cards themselves, including cards added when patients move or pages change.
    const observedCards = new Set<HTMLElement>();
    const syncCards = () => {
      const cards = new Set(cardsRoot.querySelectorAll<HTMLElement>(`[${TIMELINE_CARD_ATTRIBUTE}="true"]`));
      observedCards.forEach(card => {
        if (!cards.has(card)) {
          observer.unobserve(card);
          observedCards.delete(card);
        }
      });
      cards.forEach(card => {
        if (!observedCards.has(card)) {
          observer.observe(card);
          observedCards.add(card);
        }
      });
      measure();
    };
    const mutations = new MutationObserver(syncCards);
    mutations.observe(cardsRoot, { childList: true, subtree: true });
    syncCards();

    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heightRef, cardsRootRef, gapPx, ...deps]);

  return pageSize;
};
